import React from "react";
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

let inputControlCounter = 0;

export enum InputContextType { normal, error, warning, info };

export interface IGenericInputChangeEvent {
  (value: string): void;
}

export interface IGenericInputProps {
  autoFocus: boolean;
  value: any;
  label: string;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  helpText?: string;
  hint?: string;
  context?: InputContextType;
  left?: React.ReactNode | null;
  right?: React.ReactNode | null;
  onClick?: React.MouseEventHandler;
  onChange?: IGenericInputChangeEvent;
  onKeyDown?: React.KeyboardEventHandler;
  onEnter?: React.ReactEventHandler;
  onLeave?: React.ReactEventHandler;
};

export interface IGenericInputState {
  active: boolean;
};

export interface IGenericInput extends React.Component {
  inputId: string;
  renderLeftElement(): React.ReactNode | null;
  renderRightElement(): React.ReactNode | null;
  renderInputElement(): React.ReactNode;
  renderLabelElement(): React.ReactNode;
  renderHelpTextElement(): React.ReactNode;
}

abstract class GenericInput extends React.Component<IGenericInputProps, IGenericInputState> implements IGenericInput {
  inputId = "";

  state: IGenericInputState = {
    active: false
  };

  static defaultProps: IGenericInputProps = {
    autoFocus: false,
    value: "",
    label: "Generic Input",
    disabled: false,
    loading: false,
    helpText: "",
    hint: "",
    context: InputContextType.normal,
    left: null,
    right: null,
    required: false
  }

  constructor(props: Readonly<IGenericInputProps> | IGenericInputProps) {
    super(props);
    this.inputId = `zeon-input-control-${inputControlCounter++}`;
    this.handleFocusEvent = this.handleFocusEvent.bind(this);
    this.handleBlurEvent = this.handleBlurEvent.bind(this);
    this.handleChangeEvent = this.handleChangeEvent.bind(this);
  };

  abstract renderInputElement(): React.ReactNode;

  renderLeftElement(): React.ReactNode {
    if (!this.props.left) return null;
    return (<InputAdornment position="start">{this.props.left}</InputAdornment>);
  }

  renderRightElement(): React.ReactNode {
    if (!this.props.right) return null;
    return (<InputAdornment position="end">{this.props.right}</InputAdornment>);
  }

  renderLabelElement(): React.ReactNode {
    return <InputLabel htmlFor={this.inputId}>{
      this.props.required ?
        <b>{this.props.label}</b> :
        this.props.label
    }</InputLabel>
  }

  renderHelpTextElement(): React.ReactNode {
    return <span style={{ fontSize: 'x-small' }}>{this.props.helpText || " "}&nbsp;</span>;
  };

  calculateStyles(): object {
    const result = {
      color: this.props.disabled || this.props.loading ? "text.disabled" : "text.primary"
    };
    if (this.props.context === InputContextType.error) result['color'] = "error.main";
    else if (this.props.context === InputContextType.warning) result['color'] = "warning.main";
    else if (this.props.context === InputContextType.info) result['color'] = "primary.main";
    return result;
  }

  handleChangeEvent(e: React.ChangeEvent<Element>) {
    if (this.props.onChange) this.props.onChange((e.currentTarget as HTMLInputElement).value);
  }

  handleFocusEvent(e: React.FocusEvent<Element>) {
    if (this.props.disabled || this.props.loading) return;
    const handler = this.props.onEnter;
    this.setState(Object.assign({}, this.state, { active: true }), () => {
      if (handler) handler(e);
    });
  }

  handleBlurEvent(e: React.FocusEvent<Element>) {
    if (this.props.disabled || this.props.loading) return;
    const handler = this.props.onLeave;
    this.setState(Object.assign({}, this.state, { active: false }), () => {
      if (handler) handler(e);
    });
  }

  render() {
    return (
      <Tooltip title={this.props.hint} arrow>
        <FormControl variant="standard" fullWidth sx={this.calculateStyles()}>
          {this.renderLabelElement()}
          {this.renderInputElement()}
          {this.renderHelpTextElement()}
        </FormControl>
      </Tooltip>
    );
  }
}

export default GenericInput;